/**
 * Uniblock backend adapter.
 *
 * BYOK pass-through to the Uniblock Unified API
 * (docs: https://docs.uniblock.dev). Used in two ways:
 *
 *   1. The chat / code agents call `uniblockCall()` directly to fetch real
 *      on-chain data when answering the user, instead of hallucinating values.
 *   2. The same agents are handed `UNIBLOCK_AGENT_BRIEF` as system context so
 *      any code they generate for the user references real endpoint paths,
 *      not invented ones, and routes through the user's own Uniblock key.
 *
 * Ported from _Integrate/uniblock-backend-studio (server.ts + docs). Front-end
 * was intentionally not brought across — only the backend logic.
 *
 * Auth: Uniblock takes the key on the `X-API-KEY` header. Our shared proxy
 * (netlify/functions/proxy.ts) maps `authStyle: 'x-api-key'` to that header.
 */
import { proxy } from './common';
import { getKey } from '../lib/keys';

export const UNIBLOCK_BASE_PROD = 'https://api.uniblock.dev/uni/v1';
export const UNIBLOCK_BASE_TEST = 'https://api-test.uniblock.dev/uni/v1';

/** Default to prod; the agent can flip to test when iterating. */
export const UNIBLOCK_BASE = UNIBLOCK_BASE_PROD;

/** Curated map of Uniblock unified-API endpoint paths the agent may invoke
 *  or reference when generating BYOK apps. Paths are relative to UNIBLOCK_BASE.
 *  This list is intentionally a subset — the canonical reference lives at
 *  https://docs.uniblock.dev. Agents must NOT invent paths outside this map
 *  without first consulting the docs. */
export const UNIBLOCK_ENDPOINTS = {
  marketData: {
    tokenPrice:           { method: 'GET', path: '/market-data/price' },
    tokenPriceRange:      { method: 'GET', path: '/market-data/price-range' },
    token24hVolume:       { method: 'GET', path: '/market-data/volume-24hr' },
    tokenMarketCap:       { method: 'GET', path: '/market-data/market-cap' },
    tokenMarketChart:     { method: 'GET', path: '/market-data/market-chart' },
    tokenHistorySnapshot: { method: 'GET', path: '/market-data/history-snapshot' },
    trendingTokens:       { method: 'GET', path: '/market-data/trending' },
  },
  token: {
    addressPortfolio:     { method: 'GET', path: '/token/portfolio' },
    addressBalances:      { method: 'GET', path: '/token/balances' },
    addressBalanceHistory:{ method: 'GET', path: '/token/balance-history' },
    addressTransfers:     { method: 'GET', path: '/token/transfers' },
    addressAllowances:    { method: 'GET', path: '/token/allowances' },
    tokenMetadata:        { method: 'GET', path: '/token/metadata' },
  },
  nft: {
    walletNftBalances:    { method: 'GET', path: '/nft/wallet/balances' },
    walletNftCollections: { method: 'GET', path: '/nft/wallet/collections' },
    walletNftTransfers:   { method: 'GET', path: '/nft/wallet/transfers' },
    nftMetadata:          { method: 'GET', path: '/nft/metadata' },
    nftOwners:            { method: 'GET', path: '/nft/owners' },
    nftSales:             { method: 'GET', path: '/nft/sales' },
    nftTransfers:         { method: 'GET', path: '/nft/transfers' },
    collectionFloorPrices:{ method: 'GET', path: '/nft/collection/floor-prices' },
    collectionMetadata:   { method: 'GET', path: '/nft/collection/metadata' },
    collectionNfts:       { method: 'GET', path: '/nft/collection/nfts' },
  },
  scan: {
    addressEtherBalance:  { method: 'GET', path: '/scan/address/ether-balance' },
    addressErc20Balance:  { method: 'GET', path: '/scan/address/erc-20-balance' },
    addressTransactions:  { method: 'GET', path: '/scan/address/transactions' },
    addressErc20Transfers:{ method: 'GET', path: '/scan/address/erc-20-transfers' },
    addressErc721Transfers:{ method: 'GET', path: '/scan/address/erc-721-transfers' },
    gasPriceTiers:        { method: 'GET', path: '/scan/gas-price-tiers' },
    contractAbi:          { method: 'GET', path: '/scan/contract/abi' },
    contractSourceCode:   { method: 'GET', path: '/scan/contract/source-code' },
    nativeTokenPrice:     { method: 'GET', path: '/scan/native-token-price' },
  },
  jsonRpc: {
    send: { method: 'POST', path: '/json-rpc' },
  },
} as const;

export interface UniblockCallOpts {
  /** Path relative to UNIBLOCK_BASE — e.g. `/market-data/price`. */
  path: string;
  method?: 'GET' | 'POST';
  /** Query params, encoded into the URL. */
  query?: Record<string, string | number | boolean | undefined | null>;
  /** POST body (JSON). */
  body?: unknown;
  /** Override base — pass UNIBLOCK_BASE_TEST to hit the test sandbox. */
  base?: string;
  /** Optional override; otherwise pulled from BYOK vault. */
  apiKey?: string;
}

/**
 * Make a real call to the Uniblock unified API on behalf of the agent.
 * Throws if the user hasn't saved a Uniblock key in the BYOK vault.
 */
export async function uniblockCall<T = unknown>(opts: UniblockCallOpts): Promise<T> {
  const apiKey = opts.apiKey ?? (await getKey('uniblock'));
  if (!apiKey) {
    throw new Error(
      'No Uniblock API key found. Add it in the API keys modal (BYOK) — get a key at https://dashboard.uniblock.dev'
    );
  }

  const base = opts.base ?? UNIBLOCK_BASE;
  const url = new URL(base.replace(/\/+$/, '') + (opts.path.startsWith('/') ? opts.path : `/${opts.path}`));
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v === undefined || v === null) continue;
      url.searchParams.set(k, String(v));
    }
  }

  return await proxy<T>({
    provider: 'uniblock',
    endpoint: url.toString(),
    method: opts.method ?? 'GET',
    body: opts.method === 'POST' ? opts.body : undefined,
    apiKey,
    authStyle: 'x-api-key',
  });
}

/** Human-readable brief that gets injected into the chat / code agent's
 *  system prompt. Tells the agent (1) Uniblock exists and is the user's
 *  on-chain data source, (2) the real base URL + auth header, (3) the
 *  endpoint catalog above, and (4) BYOK rules for any code it generates. */
export const UNIBLOCK_AGENT_BRIEF = `
## Uniblock — on-chain data layer (available to you)

You have access to **Uniblock** (https://docs.uniblock.dev), a unified BYOK
API for on-chain crypto data across EVM chains and Solana. The user has
either added a Uniblock key in this app's "your api keys" modal, or you
should instruct them to. Use Uniblock whenever the user asks about wallets,
balances, tokens, NFTs, prices, gas, contracts, or transactions — never
fabricate values; either call Uniblock or generate code that does.

**Real base URL**: \`${UNIBLOCK_BASE_PROD}\` (prod) / \`${UNIBLOCK_BASE_TEST}\` (test)
**Auth header**:  \`X-API-KEY: <user's uniblock key>\`

**Real endpoint catalog** (relative paths under the base URL above). Do NOT
invent paths outside this list — if you need something else, link the user
to the Uniblock docs instead of guessing.

- Market data:
  - GET /market-data/price             — current token price (symbol/id/chainId+address)
  - GET /market-data/price-range       — price over a time range
  - GET /market-data/volume-24hr       — 24h volume
  - GET /market-data/market-cap        — market cap
  - GET /market-data/market-chart      — price+volume+mcap over range with granularity
  - GET /market-data/history-snapshot  — snapshot at a given date
  - GET /market-data/trending          — trending tokens
- Token / wallet:
  - GET /token/portfolio               — wallet portfolio
  - GET /token/balances                — native + fungible balances for address
  - GET /token/balance-history         — historical balances
  - GET /token/transfers               — token transfers for address
  - GET /token/allowances              — ERC-20 allowances
  - GET /token/metadata                — token metadata
- NFT:
  - GET /nft/wallet/balances           — NFTs held by wallet
  - GET /nft/wallet/collections        — collections owned by wallet
  - GET /nft/wallet/transfers          — wallet NFT transfer history
  - GET /nft/metadata                  — single NFT metadata
  - GET /nft/owners                    — owners of a specific NFT
  - GET /nft/sales                     — recent NFT sales
  - GET /nft/transfers                 — NFT transfers
  - GET /nft/collection/floor-prices   — collection floor by marketplace
  - GET /nft/collection/metadata       — collection metadata
  - GET /nft/collection/nfts           — NFTs in collection
- Scan (EVM explorer style):
  - GET /scan/address/ether-balance    — native balance for address
  - GET /scan/address/erc-20-balance   — ERC-20 balance for address/contract
  - GET /scan/address/transactions     — tx list for address
  - GET /scan/address/erc-20-transfers — ERC-20 transfers
  - GET /scan/address/erc-721-transfers— ERC-721 transfers
  - GET /scan/gas-price-tiers          — safe/proposed/fast gas
  - GET /scan/contract/abi             — verified contract ABI
  - GET /scan/contract/source-code     — verified source
  - GET /scan/native-token-price       — latest native token price
- Raw JSON-RPC:
  - POST /json-rpc                     — multi-chain JSON-RPC passthrough

### When you generate a BYOK crypto app for the user

ANY crypto / on-chain app you generate MUST use Uniblock.dev as its data
source — there are no exceptions. Every generated app MUST include the
**Uniblock BYOK Key Panel** described below. It is non-negotiable.

**Initial call rule.** When the app first mounts, fire exactly **one** real
call to the relevant Uniblock endpoint(s) using whatever key is already in
\`localStorage.uniblock_api_key\` (or, if the key is empty, render the
dashboard's empty state and open the key panel automatically). This makes
the dashboard real on first paint instead of waiting for a manual trigger.

**Mandatory Uniblock BYOK Key Panel (must appear in EVERY crypto app):**

1. A **discrete key icon** is fixed in the **top-right corner** of the app
   (\`position: fixed; top: 12px; right: 12px; z-index: 9999\`). Use an SVG
   key glyph, ~20px, low-contrast so it stays unobtrusive. Tooltip /
   \`aria-label\`: "Uniblock API key & refresh rate".
2. Clicking that icon opens a **modal popup centered from all four margins**
   (a true app-level overlay: \`position: fixed; inset: 0\`, dimmed
   backdrop, the dialog itself centered with flex/grid — equal gap on top,
   bottom, left, right). It must trap focus, close on Esc, and close on
   backdrop click.
3. The popup contains, in order:
   a. A short heading "Uniblock API Key".
   b. A password-style input bound to \`localStorage.uniblock_api_key\`
      (placeholder \`uni-…\`). A "Get a key →" link to
      https://dashboard.uniblock.dev.
   c. A "Refresh frequency" control — let the user pick from at minimum:
      \`30s\`, \`1m\`, \`5m\`, \`15m\`, \`1h\`, \`6h\`, \`daily\`. Persist the
      choice as \`localStorage.uniblock_refresh_ms\` (number of ms).
   d. Save / Cancel buttons. Save persists both values, closes the modal,
      and immediately re-runs the dashboard's Uniblock calls.
4. The app's data layer reads \`uniblock_refresh_ms\` and re-runs the
   Uniblock fetch on a \`setInterval\` of that length. If the user picks
   "daily", use \`86_400_000\` ms; record \`last_run_at\` in localStorage so
   reloads don't restart the clock prematurely.
5. All Uniblock calls hit the real base URL \`${UNIBLOCK_BASE_PROD}\` with
   the \`X-API-KEY\` header set from \`localStorage.uniblock_api_key\`.
6. Use only paths from the catalog above. If a needed path is not in the
   catalog, add a TODO comment referencing https://docs.uniblock.dev rather
   than inventing one.
7. Render an error state for HTTP 401 / 403 that re-opens the key panel
   automatically.
8. The app is one-click deployable to Netlify (plain Vite + React, no
   server secrets). Include a minimal \`netlify.toml\` and a short README
   explaining the BYOK flow and where the key icon lives.
9. Never hard-code a key. Never read from \`process.env\` in client code.
   Never substitute another data provider for Uniblock.

### When you call Uniblock yourself during this conversation

You can invoke endpoints live via the host app's \`uniblockCall()\` helper.
Prefer one live call over describing what a call would return.
`;
